"use client";
import useRedirect from "@/hooks/useRedirect";
import { motion } from "motion/react";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react"; 
import { Home, Clock, Search, HelpCircle, ArrowLeft } from "lucide-react";

// Error tracking for analytics
export function reportNotFoundError(url: string) {
  if (typeof window !== "undefined") {
    console.error(`404 error occurred for path: ${url}`);
  }
}

// 3D animated 404 component
const AnimatedError = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      setMousePosition({
        x: (clientX - centerX) / 50,
        y: (clientY - centerY) / 50,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="relative mb-6 flex items-center justify-center">
      <motion.div
        className="flex"
        style={{
          transformStyle: "preserve-3d",
        }}
        animate={{
          rotateX: mousePosition.y,
          rotateY: -mousePosition.x,
        }}
        transition={{ type: "spring", damping: 20 }}
      >
        <h1 className="text-8xl md:text-9xl font-extrabold tracking-wider">
          <span
            style={{
              background:
                "linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "2px 2px 0px rgba(0,0,0,0.1)",
            }}
          >
            404
          </span>
        </h1>
      </motion.div>
    </div>
  );
};

// Path display component
const PathDisplay = ({ path }: { path: string }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mb-6 p-3 bg-error/10 dark:bg-error/20 rounded-lg overflow-hidden"
    >
      <code className="text-sm text-error font-mono block overflow-x-auto">
        {path}
      </code>
    </motion.div>
  );
};

// Quick links component
const QuickLinks = () => {
  const links = [
    { icon: <Home size={16} />, text: "Home", href: "/" },
    { icon: <HelpCircle size={16} />, text: "Help", href: "/help" },
    { icon: <Search size={16} />, text: "Sitemap", href: "/sitemap" },
  ];

  return (
    <div className="flex justify-center gap-3 my-4">
      {links.map((link, index) => (
        <Link key={index} href={link.href}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-1 px-3 py-2 bg-gray-50 dark:bg-gray-700/40 rounded-md"
          >
            <span className="text-secondary">{link.icon}</span>
            <span className="text-sm font-medium">{link.text}</span>
          </motion.div>
        </Link>
      ))}
    </div>
  );
};

function NotFoundClient() {
  const redirectDelay = 15000;
  const { countdown, disableRedirect } = useRedirect(redirectDelay);
  const [currentPath, setCurrentPath] = useState("");

  // Capture current path for error reporting
  useEffect(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      setCurrentPath(path);
      reportNotFoundError(path);
    }
  }, []);

  // Memoized click handler to prevent unnecessary re-renders
  const handleDisableRedirect = useCallback(() => {
    disableRedirect();
  }, [disableRedirect]);

  return (
    <div className="flex items-center justify-center min-h-screen px-4 py-8 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full p-6 md:p-8 bg-white dark:bg-gray-800 shadow-xl rounded-lg text-center"
      >
        <AnimatedError />

        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-2xl font-bold text-gray-900 dark:text-white mb-3"
        >
          Page Not Found
        </motion.h2>

        <p className="text-gray-700 dark:text-gray-300 mb-4">
          We couldn't find the page you were looking for.
        </p>

        {currentPath && <PathDisplay path={currentPath} />}

        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mb-5">
          <Link href="/" passHref>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <button className="w-full bg-secondary hover:bg-secondary/90 text-white font-medium">
                <ArrowLeft size={16} className="mr-2" /> Back to Home
              </button>
            </motion.div>
          </Link>

          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <button
              onClick={handleDisableRedirect}
              className="w-full border-primary text-primary hover:bg-primary/10"
            >
              <Clock size={16} className="mr-2" />
              {countdown > 0 ? "Stop Redirect" : "Redirect Stopped"}
            </button>
          </motion.div>
        </div>

        <QuickLinks />

        {countdown > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 flex items-center justify-center space-x-2"
          >
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full w-full overflow-hidden">
              <motion.div
                className="h-full bg-primary"
                initial={{ width: "100%" }}
                animate={{ width: `${(countdown / redirectDelay) * 100}%` }}
                transition={{ duration: 1 }}
              />
            </div>
            <span className="text-sm font-medium text-mutedText w-12">
              {countdown}s
            </span>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}

// Main NotFoundPage component
export default function NotFoundPage() {
  return <NotFoundClient />;
}
